import React, {Component} from 'react';
import {observer} from 'mobx-react';
import I18n from 'app/i18n';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {casinoGamesStore} from 'app/store/global';
import CasinoRandomSlotCard from 'app/components/pages/casino/marketing-blocks/CasinoRandomSlotCard';
import {captureCasinoGameLaunchRow} from 'app/services/CasinoServices';
import {isDesktop} from 'app/utils';
import {
    computeSpinEndScrollLeft,
    easeOutSine,
    getRandomSlotCenteredIndexFromScrollLeft,
    getRandomSlotScrollLeftToCenterIndex,
    getRandomSlotSingleSetWidthPx,
    repeatGamesForCarousel,
    roundScrollLeft,
    scrollLeftInPeriod
} from 'app/components/pages/casino/marketing-blocks/CasinoMarketingUtils';

interface CasinoRandomSlotProps {
    style?: React.CSSProperties;
}

interface CasinoRandomSlotState {
    centeredGameIndex: number;
    isSpinning: boolean;
    selectedIndex: number | null;
    viewportWidth: number | null;
}

const REPEAT_COUNT = 5;
const SPIN_DURATION_MS = 3000;

@observer
export default class CasinoRandomSlot extends Component<CasinoRandomSlotProps, CasinoRandomSlotState> {
    blockRef = React.createRef<HTMLDivElement>();

    trackRef = React.createRef<HTMLDivElement>();

    private justFinishedSpin = false;

    private scrollRafId: number | null = null;

    private initialScrollRafId: number | null = null;

    private selectedScrollRafId: number | null = null;

    private spinRafId: number | null = null;

    private initialCarouselScrollDone = false;

    constructor(props: CasinoRandomSlotProps) {
        super(props);
        this.state = {
            centeredGameIndex: 0,
            isSpinning: false,
            selectedIndex: null,
            viewportWidth: null
        };
    }

    componentDidMount(): void {
        this.updateViewportWidth();
        window.addEventListener('resize', this.updateViewportWidth);
        this.applyInitialScrollAndSyncCentered();
    }

    componentDidUpdate(_prevProps: Readonly<CasinoRandomSlotProps>, prevState: CasinoRandomSlotState): void {
        if (this.syncViewportWidthOnUpdate()) {
            if (!this.initialCarouselScrollDone) {
                this.applyInitialScrollAndSyncCentered();
            }

            return;
        }

        this.syncScrollOnSelectedIndexChange(prevState);

        if (!this.initialCarouselScrollDone) {
            this.applyInitialScrollAndSyncCentered();
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.updateViewportWidth);
        this.initialCarouselScrollDone = false;

        this.cancelAllPendingAnimationFrames();
    }

    private cancelAllPendingAnimationFrames(): void {
        if (this.scrollRafId !== null) {
            cancelAnimationFrame(this.scrollRafId);
            this.scrollRafId = null;
        }

        if (this.initialScrollRafId !== null) {
            cancelAnimationFrame(this.initialScrollRafId);
            this.initialScrollRafId = null;
        }

        if (this.selectedScrollRafId !== null) {
            cancelAnimationFrame(this.selectedScrollRafId);
            this.selectedScrollRafId = null;
        }

        if (this.spinRafId !== null) {
            cancelAnimationFrame(this.spinRafId);
            this.spinRafId = null;
        }
    }

    get singleSetWidth(): number {
        return getRandomSlotSingleSetWidthPx(casinoGamesStore.randomSlotGames.length);
    }

    private applyInitialScrollAndSyncCentered = (): void => {
        if (this.initialScrollRafId !== null) {
            cancelAnimationFrame(this.initialScrollRafId);
        }

        this.initialScrollRafId = requestAnimationFrame(() => {
            this.initialScrollRafId = null;

            if (!this.initialCarouselScrollDone) {
                this.applyInitialCarouselScrollOnce();
                this.syncCenteredFromTrack();
            }
        });
    };

    private applyInitialCarouselScrollOnce = (): void => {
        const track = this.trackRef.current;
        const games = casinoGamesStore.randomSlotGames;

        if (!track || games.length === 0 || track.clientWidth === 0) {
            return;
        }

        this.initialCarouselScrollDone = true;
        track.scrollLeft = roundScrollLeft(
            getRandomSlotScrollLeftToCenterIndex(games.length, track.clientWidth)
        );
    };

    private setTrackScrollLeftAndSync = (scrollLeft: number): void => {
        const track = this.trackRef.current;

        if (!track) {
            return;
        }

        track.scrollLeft = scrollLeft;
        this.syncCenteredFromTrack();
    };

    handleCarouselScroll = (): void => {
        if (this.scrollRafId !== null) {
            return;
        }

        this.scrollRafId = requestAnimationFrame(() => {
            this.scrollRafId = null;
            const track = this.trackRef.current;

            if (!track || this.state.isSpinning) {
                return;
            }

            if (track.scrollLeft >= this.singleSetWidth) {
                this.setTrackScrollLeftAndSync(roundScrollLeft(track.scrollLeft - this.singleSetWidth));

                return;
            }

            this.syncCenteredFromTrack();
        });
    };

    private syncCenteredFromTrack = (): void => {
        const track = this.trackRef.current;

        if (this.state.isSpinning || !track) {
            return;
        }

        const next = this.computeCenteredGameIndex(track.scrollLeft);

        if (next !== this.state.centeredGameIndex) {
            this.setState({centeredGameIndex: next});
        }
    };

    syncViewportWidthOnUpdate = (): boolean => {
        if (this.state.viewportWidth !== null || !this.trackRef.current) {
            return false;
        }

        this.updateViewportWidth();
        return true;
    };

    syncScrollOnSelectedIndexChange = (prevState: CasinoRandomSlotState): void => {
        const {isSpinning, selectedIndex} = this.state;

        if (selectedIndex === null || prevState.selectedIndex === selectedIndex || isSpinning || !this.trackRef.current) {
            return;
        }

        if (this.justFinishedSpin) {
            this.justFinishedSpin = false;
            return;
        }

        if (this.selectedScrollRafId !== null) {
            cancelAnimationFrame(this.selectedScrollRafId);
        }

        this.selectedScrollRafId = requestAnimationFrame(() => {
            this.selectedScrollRafId = null;
            this.scrollSelectedToCenter();
        });
    };

    getViewportWidth = (track: HTMLDivElement): number => this.state.viewportWidth ?? track.clientWidth;

    updateViewportWidth = (): void => {
        const track = this.trackRef.current;

        if (!track) {
            return;
        }

        const measuredViewportWidth = track.clientWidth;

        if (measuredViewportWidth === this.state.viewportWidth) {
            return;
        }

        this.setState({viewportWidth: measuredViewportWidth}, () => {
            this.syncCenteredFromTrack();
        });
    };

    runSpinAnimation = (winnerIndex: number): void => {
        const track = this.trackRef.current;
        const games = casinoGamesStore.randomSlotGames;
        const {singleSetWidth} = this;

        if (!track || games.length === 0) {
            return;
        }

        const viewportWidth = this.getViewportWidth(track);
        const canonicalStart = roundScrollLeft(scrollLeftInPeriod(track.scrollLeft, singleSetWidth));

        if (canonicalStart !== track.scrollLeft) {
            track.scrollLeft = canonicalStart;
        }

        const targetScroll = getRandomSlotScrollLeftToCenterIndex(winnerIndex + games.length, viewportWidth);
        const endLeft = roundScrollLeft(
            computeSpinEndScrollLeft(canonicalStart, singleSetWidth, targetScroll)
        );

        this.scheduleSpinRafLoop({canonicalStart, endLeft, track, winnerIndex});
    };

    private scheduleSpinRafLoop(params: {
        canonicalStart: number;
        endLeft: number;
        track: HTMLDivElement;
        winnerIndex: number;
    }): void {
        const {canonicalStart, endLeft, track, winnerIndex} = params;
        const startTime = performance.now();

        const tick = (now: number): void => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / SPIN_DURATION_MS, 1);

            if (progress < 1) {
                const eased = easeOutSine(progress);
                track.scrollLeft = roundScrollLeft(
                    canonicalStart + (endLeft - canonicalStart) * eased
                );
                this.spinRafId = requestAnimationFrame(tick);
            } else {
                this.spinRafId = null;
                track.scrollLeft = roundScrollLeft(endLeft);
                this.justFinishedSpin = true;
                const centeredGameIndex = this.computeCenteredGameIndex(track.scrollLeft);

                this.setState({
                    centeredGameIndex,
                    isSpinning: false,
                    selectedIndex: winnerIndex
                });
            }
        };

        if (this.spinRafId !== null) {
            cancelAnimationFrame(this.spinRafId);
        }

        this.spinRafId = requestAnimationFrame(tick);
    }

    scrollSelectedToCenter = (): void => {
        const track = this.trackRef.current;
        const {selectedIndex} = this.state;

        if (!track || selectedIndex === null) {
            return;
        }

        const viewportWidth = this.getViewportWidth(track);
        const targetIndex = selectedIndex + casinoGamesStore.randomSlotGames.length;

        this.setTrackScrollLeftAndSync(getRandomSlotScrollLeftToCenterIndex(targetIndex, viewportWidth));
    };

    handleSpin = (): void => {
        const {isSpinning} = this.state;
        const games = casinoGamesStore.randomSlotGames;

        if (games.length === 0 || isSpinning) {
            return;
        }

        const winnerIndex = Math.floor(Math.random() * games.length);

        this.setState({isSpinning: true, selectedIndex: null}, () => {
            this.runSpinAnimation(winnerIndex);
        });
    };

    setIframeGridPlacement = (): void => {
        if (!isDesktop()) {
            return;
        }

        captureCasinoGameLaunchRow(this.blockRef.current);
    };

    computeCenteredGameIndex(scrollLeft: number): number {
        const games = casinoGamesStore.randomSlotGames;
        const repeatedCount = games.length * REPEAT_COUNT;

        if (repeatedCount === 0) {
            return 0;
        }

        const track = this.trackRef.current;

        if (!track) {
            return 0;
        }

        const viewportWidth = this.getViewportWidth(track);

        return getRandomSlotCenteredIndexFromScrollLeft(scrollLeft, viewportWidth, repeatedCount);
    }

    render(): React.ReactNode {
        const {centeredGameIndex, isSpinning} = this.state;
        const {style} = this.props;
        const games = casinoGamesStore.randomSlotGames;
        const loading = casinoGamesStore.loadingNewGames && games.length === 0;
        const repeatedGames = repeatGamesForCarousel(games, REPEAT_COUNT);

        if (loading) {
            return (
                <div
                    className="casino-random-slot casino-random-slot--loading marketing-block"
                    style={style}
                >
                    <div className="casino-random-slot__loading-bar" />
                </div>
            );
        }

        if (games.length === 0) {
            return (
                <div className="casino-random-slot casino-random-slot--empty marketing-block" style={style}>
                    <div className="casino-random-slot__empty-inner">
                        <SpriteIcon name="#svg-dice" className="svg-default casino-random-slot__icon" />
                        <p className="casino-random-slot__empty-text">{I18n.t('empty_games1')}</p>
                    </div>
                </div>
            );
        }

        return (
            <div ref={this.blockRef} className="casino-random-slot marketing-block" style={style}>
                <div className="casino-random-slot__panel">
                    <div className="casino-random-slot__panel-top">
                        <SpriteIcon name="#svg-dice" className="svg-default casino-random-slot__icon" />
                        <h4 className="casino-random-slot__title">{I18n.t('random_game')}</h4>
                    </div>
                    <button
                        type="button"
                        className="casino-random-slot__spin-btn"
                        onClick={this.handleSpin}
                        disabled={isSpinning}
                    >
                        {I18n.t('spin')}
                    </button>
                </div>
                <div className="casino-random-slot__carousel-wrap">
                    <div
                        ref={this.trackRef}
                        className="casino-random-slot__carousel"
                        onScroll={this.handleCarouselScroll}
                    >
                        {repeatedGames.map((game, gameIndex) => <CasinoRandomSlotCard
                            key={`random-slot-${gameIndex}-${game.id}`}
                            game={game}
                            isSelected={gameIndex === centeredGameIndex}
                            setIframeGridPlacement={this.setIframeGridPlacement}
                        />)}
                    </div>
                    <div className="casino-random-slot__center-frame">
                        <SpriteIcon name="#svg-triangle-full-soft" className="svg-default casino-random-slot__center-frame-icon top" />
                        <SpriteIcon name="#svg-triangle-full-soft" className="svg-default casino-random-slot__center-frame-icon bottom" />
                    </div>
                </div>
            </div>
        );
    }
}
